import { useState, useEffect, useRef, FormEvent, useContext } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { acquireMediaRegistry, releaseMediaRegistry, stopStreamsSince } from "../../utils/mediaStreamRegistry";
import { X } from "lucide-react";
import axios from "../../axios-config";
import { toast } from "react-toastify";
import { UserContext } from "../../contexts/UserContext";
import { createPortal } from "react-dom";

interface QRScannerProps {
  onClose: () => void;
  onRedeemSuccess?: () => void;
  mode: "redeem" | "reissue";
}

export default function QRScanner({ onClose, onRedeemSuccess, mode = "redeem" }: QRScannerProps) {
  const { setUserProfile, refreshProfile } = useContext(UserContext);
  const [couponCode, setCouponCode] = useState("");
  const [error, setError] = useState("");
  const [isCameraLoading, setIsCameraLoading] = useState(false);

  // Screen size detection for responsive modal sizing
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });

  // Set autoClose false for reissue mode, true by default for redeem mode
  const [autoClose, setAutoClose] = useState(mode === "reissue" ? false : true);
  const autoCloseRef = useRef(autoClose);
  useEffect(() => { autoCloseRef.current = autoClose; }, [autoClose]);

  const scannerRef = useRef<Html5Qrcode | null>(null);
  const scanInProgressRef = useRef(false);
  const cancelledRef = useRef(false); // becomes true once user closes or unmounts
  const startingRef = useRef(false); // true while start() promise in flight
  const SCANNER_ID = "qr-scanner-element";
  // Timestamp when component mounted (for selective stream cleanup)
  const startedAtRef = useRef<number>(performance.now());
  const registryHandleRef = useRef<ReturnType<typeof acquireMediaRegistry> | null>(null);
  // Container ref so we can size the qrbox precisely relative to the dashed overlay frame
  const scanContainerRef = useRef<HTMLDivElement | null>(null);

  // Window resize listener for responsive modal sizing
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate responsive dimensions based on screen size
  const getModalDimensions = () => {
    const { width, height } = windowSize;
    
    // For very small screens (mobile phones), use more of the available space
    if (height <= 600) {
      return {
        maxHeight: '90vh',
        scannerHeight: '35vh',
        padding: 'p-2',
        fontSize: 'text-xs',
        spacing: 'space-y-2'
      };
    }
    
    // For Chromebook-like devices (wide but not very tall - common issue case)
    if (height <= 768 && width >= 1200) {
      return {
        maxHeight: '85vh',
        scannerHeight: '45vh',
        padding: 'p-3',
        fontSize: 'text-sm',
        spacing: 'space-y-2'
      };
    }
    
    // For small tablets or landscape phones
    if (height <= 768) {
      return {
        maxHeight: '85vh',
        scannerHeight: '40vh',
        padding: 'p-3',
        fontSize: 'text-sm',
        spacing: 'space-y-2'
      };
    }
    
    // For medium screens
    if (height <= 1024) {
      return {
        maxHeight: '80vh',
        scannerHeight: '40vh',
        padding: 'p-4',
        fontSize: 'text-sm',
        spacing: 'space-y-3'
      };
    }
    
    // For larger screens, use the original sizing
    return {
      maxHeight: '95vh',
      scannerHeight: '50vh',
      padding: 'p-6',
      fontSize: 'text-base',
      spacing: 'space-y-4'
    };
  };

  const modalDimensions = getModalDimensions();

  useEffect(() => {
    // Acquire global registry (patch getUserMedia) and remember handle
    registryHandleRef.current = acquireMediaRegistry();
    startedAtRef.current = performance.now();
    startScanner();
    return () => {
      cancelledRef.current = true;
      stopScanner();
      cleanupMediaStreams();
      // Ensure all newly opened tracks (via patched getUserMedia) are stopped
      stopStreamsSince(startedAtRef.current);
      // delayed extra cleanup in case video element attaches late
      setTimeout(cleanupMediaStreams, 300);
      setTimeout(cleanupMediaStreams, 1200);
      setTimeout(() => stopStreamsSince(startedAtRef.current), 600);
      releaseMediaRegistry();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function startScanner() {
    setError("");
    setIsCameraLoading(true);
    startingRef.current = true;
    try {
      const scanner = new Html5Qrcode(SCANNER_ID, { verbose: false, formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE] });
      scannerRef.current = scanner;
      const devices = await Html5Qrcode.getCameras();
      if (!devices || devices.length === 0) throw new Error("No cameras found");
      if (cancelledRef.current) {
        await disposeScanner(scanner);
        return;
      }
      const cameraId = devices[0].id;
      
      // Size the qrbox so it sits just inside the dashed overlay (inset-4 => 16px each side)
      // Provide an extra small internal buffer so the library's drawn square does not collide with the dashed border visually
      let qrBoxSize: number;
      const containerRect = scanContainerRef.current?.getBoundingClientRect();
      if (containerRect && containerRect.width > 0 && containerRect.height > 0) {
        const dashedInset = 16; // inset-4 => 1rem (16px)
        const innerBuffer = 8;  // extra breathing room inside dashed frame
        const availableWidth = containerRect.width - (dashedInset * 2) - (innerBuffer * 2);
        const availableHeight = containerRect.height - (dashedInset * 2) - (innerBuffer * 2);
        qrBoxSize = Math.round(Math.min(availableWidth, availableHeight));
        // Safety clamp
        qrBoxSize = Math.max(180, Math.min(qrBoxSize, 640));
      } else {
        // Fallback: approximate from viewport if measurement not ready yet
        const scannerHeightStr = (modalDimensions?.scannerHeight || "50vh").toString();
        const vhMatch = /(\d+)/.exec(scannerHeightStr);
        const scannerHeightPx = vhMatch ? (windowSize.height * (parseInt(vhMatch[1], 10) / 100)) : Math.min(windowSize.height * 0.5, 520);
        qrBoxSize = Math.round(Math.max(Math.min(scannerHeightPx * 0.85, windowSize.width * 0.8, 560), 200));
      }
      
      await scanner.start(
        cameraId,
        { fps: 10, qrbox: { width: qrBoxSize, height: qrBoxSize }, aspectRatio: 1 },
        onScanSuccess,
        onScanFailure
      );
      if (cancelledRef.current) {
        // Closed while starting
        await stopScanner();
      }
  // After a successful start, record current time to allow later selective stream stopping
  startedAtRef.current = performance.now();
    } catch (err: any) {
      if (!cancelledRef.current) {
        console.error("Scanner error:", err);
        setError("Unable to access camera. Please check camera permissions.");
      }
    } finally {
      startingRef.current = false;
      setIsCameraLoading(false);
    }
  }

  async function onScanSuccess(decodedText: string) {
    if (scanInProgressRef.current) return;
    scanInProgressRef.current = true;
    const success = await handleSubmit(decodedText);
    if (success) {
      if (autoCloseRef.current) {
        await stopScanner();
        onClose();
        if (onRedeemSuccess) onRedeemSuccess();
      } else {
        setCouponCode("");
        setError("");
        setTimeout(() => {
          scanInProgressRef.current = false;
        }, 1500);
      }
    } else {
      scanInProgressRef.current = false;
    }
  }

  function onScanFailure() {
    // Called continuously by the scanner; we can ignore these
  }

  async function stopScanner() {
    try {
      if (scannerRef.current) {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
      }
    } catch {
      // ignore
    } finally {
      scannerRef.current = null;
      scanInProgressRef.current = false;
      cleanupMediaStreams();
      setTimeout(cleanupMediaStreams, 200); // late cleanup
  // Stop any streams opened during component lifetime
  stopStreamsSince(startedAtRef.current);
    }
  }

  async function disposeScanner(scanner: Html5Qrcode | null) {
    if (!scanner) return;
    try { await scanner.stop(); } catch { /* ignore */ }
    try { await scanner.clear(); } catch { /* ignore */ }
    if (scannerRef.current === scanner) scannerRef.current = null;
    cleanupMediaStreams();
  stopStreamsSince(startedAtRef.current);
  }

  function cleanupMediaStreams() {
    try {
      // First local scanner element
      const el = document.getElementById(SCANNER_ID);
      const videoEls: HTMLVideoElement[] = [];
      if (el) videoEls.push(...Array.from(el.getElementsByTagName("video")));
      // Fallback: any lingering videos in body created by library
      document.querySelectorAll("video").forEach(v => {
        if (!videoEls.includes(v as HTMLVideoElement)) videoEls.push(v as HTMLVideoElement);
      });
      videoEls.forEach(v => {
        const stream = v.srcObject as MediaStream | null;
        if (stream) {
          stream.getTracks().forEach(t => { if (t.readyState === "live") t.stop(); });
          v.srcObject = null;
        }
      });
    } catch {/* ignore */}
  }

  async function handleSubmitManual(e: FormEvent) {
    e.preventDefault()
    const success = await handleSubmit()
    if (success) {
     if (autoCloseRef.current) {
        await stopScanner()
        onClose()
        if (onRedeemSuccess) onRedeemSuccess()
      } else {
        setCouponCode("")
        setError("")
        // allow another manual scan attempt
        scanInProgressRef.current = false
      }
    }
  }

  async function handleSubmit(inputCode?: string): Promise<boolean> {
    const codeToRedeem = inputCode || couponCode;
    if (!codeToRedeem || codeToRedeem.length < 5) {
      setError("Please enter a valid coupon code.");
      return false;
    }
    try {
      const endpoint = mode === "reissue"
        ? `api/Acorns/ReIssue?acornCode=${codeToRedeem}`
        : `api/Acorns/Redeem?acornCode=${codeToRedeem}`;
      const response = await axios.post(endpoint);

      setTimeout(() => {
        toast.success(
          mode === "reissue" ? "Coupon reissued successfully" : "Acorn redeemed successfully",
          { position: "top-center", autoClose: 3000 }
        );
      }, 0);

      // Update immediate fields (optimistic) then fetch full fresh profile so all balances stay in sync
      setUserProfile(prev => prev ? {
        ...prev,
        acornsRedeemed: response.data.acornsRedeemed ?? prev.acornsRedeemed,
        points: response.data.points ?? prev.points,
        badges: response.data.badges ?? prev.badges,
      } : null);

      // Fire and await a full refresh to capture any other server‑side changes (e.g., general/savings balances)
      try {
        await refreshProfile();
      } catch (e) {
        // Non-fatal; UI already updated optimistically
        console.warn("Profile refresh after redeem failed", e);
      }

      return true;
    } catch (err: any) {
      console.error(err);
      setError(
        err?.response?.data?.message || "Error processing your request. Please try again."
      );
      return false;
    }
  }

  return createPortal(
    (
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9999] flex items-center justify-center p-2">
        <div 
          className={`bg-white/90 backdrop-blur-md ${modalDimensions.padding} rounded-2xl shadow-xl w-full max-w-[95vw] sm:max-w-md lg:max-w-lg`}
          style={{ 
            maxHeight: modalDimensions.maxHeight,
            minHeight: 'fit-content',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <div className="flex justify-between items-center mb-3 flex-shrink-0">
            <h3 className={`${modalDimensions.fontSize} font-semibold text-gray-800`}>
              {mode === "reissue" ? "Reissue Acorn" : "Scan QR Code"}
            </h3>
            <button
              type="button"
              onClick={async () => {
                cancelledRef.current = true;
                await stopScanner();
                cleanupMediaStreams();
                stopStreamsSince(startedAtRef.current);
                setTimeout(cleanupMediaStreams, 300);
                setTimeout(() => stopStreamsSince(startedAtRef.current), 400);
                setTimeout(cleanupMediaStreams, 1200);
                onClose();
              }}
              className="p-1.5 hover:bg-gray-100/80 rounded-full transition-colors flex-shrink-0"
            >
              <X className="w-4 h-4 text-gray-600" />
            </button>
          </div>
          
          <div className="flex flex-col flex-1 min-h-0">
            <div className="flex items-center justify-end mb-2 flex-shrink-0">
              <label className="inline-flex items-center cursor-pointer">
                <span className={`mr-2 ${modalDimensions.fontSize} font-medium text-gray-700`}>
                  {autoClose ? "Auto close enabled" : "Keep open after scan"}
                </span>
                <input 
                  type="checkbox" 
                  checked={autoClose} 
                  onChange={(e) => setAutoClose(e.target.checked)} 
                  className="sr-only peer" 
                />
                <div className="relative w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600" />
              </label>
            </div>
            
            <div 
              ref={scanContainerRef}
              className="relative w-full bg-black/5 rounded-xl overflow-hidden flex items-center justify-center mb-3 flex-shrink-0 border-2 border-gray-300/50"
              style={{ height: modalDimensions.scannerHeight }}
            >
              {isCameraLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/5 z-10">
                  <div className="w-6 h-6 border-4 border-gray-300 border-t-gray-800 rounded-full animate-spin" />
                </div>
              )}
              <div id={SCANNER_ID} className="w-full h-full rounded-lg" />
              <div className="absolute inset-4 border-2 border-dashed border-blue-400/70 rounded-lg pointer-events-none flex items-center justify-center">
                <div className="text-white/80 text-xs bg-black/50 px-2 py-1 rounded backdrop-blur-sm">
                  Position QR code here
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2 my-3 flex-shrink-0">
              <div className="h-px bg-gray-200 flex-1" />
              <p className={`${modalDimensions.fontSize} text-gray-500`}>or enter code manually</p>
              <div className="h-px bg-gray-200 flex-1" />
            </div>
            
            <form onSubmit={handleSubmitManual} className={`${modalDimensions.spacing} flex-shrink-0`}>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={couponCode}
                  onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setError(""); }}
                  placeholder="e.g., HLA8G4L1B9ZX4"
                  className={`flex-1 px-3 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-200 focus:border-transparent transition-colors ${modalDimensions.fontSize} tracking-wider font-mono`}
                  pattern="[A-Z0-9]+"
                  maxLength={16}
                  autoComplete="off"
                  spellCheck="false"
                />
                <button
                  type="submit"
                  className={`px-4 py-2 bg-gray-800/90 text-white rounded-lg hover:bg-gray-900 transition-all flex items-center justify-center gap-2 ${modalDimensions.fontSize} font-medium whitespace-nowrap`}
                  disabled={!couponCode || couponCode.length < 5}
                >
                  {mode === "reissue" ? "Reissue" : "Redeem"}
                </button>
              </div>
            </form>
            
            {error && (
              <div className={`bg-red-50/50 backdrop-blur-sm text-red-600 p-2 rounded-lg ${modalDimensions.fontSize} text-center mt-2 flex-shrink-0`}>
                {error}
              </div>
            )}
          </div>
        </div>
      </div>
    ),
    document.body
  );
}
